import { defaultAllSelection } from "services/constants/selections";
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import {
  shiftViewFlat,
  departmentViewFlat,
  shiftViewHeader,
  departmentView,
} from "../../TeamZoneHeaders";
import { useSelector, useDispatch } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { DepartmentWise } from "./DepartmentWise";
import { ShiftWise } from "./ShiftWise";
import {
  setAttendanceShiftName,
  setAttendanceShiftId,
  setAttendanceDate,
} from "services/Redux/TeamZone";
import {
  ApprovalDateRangeFilter,
  filterBarPickerSx,
} from "components/CustomComponents/ApprovalDateRangeFilter";
import { useFilter } from "pages/FilterContext";
import { FilterSection } from "pages/FilterContext/FilterRow";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useSaveView } from "components/CustomComponents/useSaveView";
import { toFlatDisplayHeaders } from "components/CustomComponents/headerGroupUtils";
import { checkViewerPermission } from "services/utils/HelperFunction";
import moment from "moment";

export const Reports = ({ selectedComponent, shiftData, subModuleId }) => {
  const previousComponentRef = useRef(selectedComponent);
  const {
    domain,
    userDetails,
    topbarCompanyId,
    topbarCompanyName,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const { clearFilterState } = useFilter();
  const { attendanceShiftName, attendanceShiftId, attendanceDate } =
    useSelector((state) => state.teamZoneReducer);
  const dispatch = useDispatch();
  const [departmentFilterData, setDepartmentFilterData] = useState([]);
  const [employeeFilterData, setEmployeeFilterData] = useState([]);
  const [shiftName, setShiftName] = useState(attendanceShiftName);
  const [shiftId, setShiftId] = useState(attendanceShiftId);
  // Department/Shift View never load today (the picker stops at yesterday), but
  // the shared attendanceDate defaults to today and is also set by the
  // Overview tab, so clamp it to yesterday instead of fetching today.
  const [date, setDate] = useState(() => {
    const yesterday = moment().subtract(1, "days").format("YYYY-MM-DD");
    return attendanceDate && attendanceDate <= yesterday
      ? attendanceDate
      : yesterday;
  });
  const [departmentName, setDepartmentName] = useState("All Department");
  const [departmentId, setDepartmentId] = useState(String(defaultAllSelection));
  const [leavePage, setLeavePage] = useState(0);
  // "Status" (the combined submodule Department View/Shift View used to
  // share) no longer exists in the live payload -- it split into standalone
  // submodules per tab -- so check whichever one this instance is actually
  // rendering for, via the selectedComponent prop it already receives.
  let check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Attendance",
    selectedComponent,
  );
  const { result: allDepartmentDetails } = useFetch(
    `${domain}get-department/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );
  const departmentDetails = [
    {
      id: defaultAllSelection,
      department_name: "All Department",
    },
    ...allDepartmentDetails,
  ];
  const {
    result: shiftReports,
    setLoad: reCallShiftApi,
    loading: shiftLoading,
  } = useFetch(
    `${domain}shift-filter-view/?user_id=${userDetails?.id}&date=${date}&shift_id=${shiftId}&company_id=${topbarCompanyId}`,
  );
  const {
    result: departmentReports,
    setLoad: reCallDepartmentApi,
    loading,
  } = useFetch(
    `${domain}deparment-filter-view/?department_id=${departmentId}&user_id=${userDetails?.id}&date=${date}&company_id=${topbarCompanyId}`,
  );

  useEffect(() => {
    reCallDepartmentApi(true);
    reCallShiftApi(true);
  }, [topbarCompanyId, reCallDepartmentApi, reCallShiftApi]);

  useEffect(() => {
    clearFilterState();
  }, [clearFilterState]);

  useEffect(() => {
    if (previousComponentRef.current !== selectedComponent) {
      clearFilterState();
      previousComponentRef.current = selectedComponent;
    }
  }, [clearFilterState, selectedComponent]);

  const handleDepartmentName = (event, data) => {
    if (data) {
      setDepartmentId(data?.id);
      setDepartmentName(data?.label);
      recallApis();
      reCallShiftApi(true);
    } else {
      setDepartmentId("");
      setDepartmentName("");
    }
  };

  const renderComponent = () => {
    switch (selectedComponent) {
      case "Department View":
        return (
          <DepartmentWise
            departmentReports={departmentFilterData}
            loading={loading}
            setLeavePage={setLeavePage}
            leavePage={leavePage}
            headers={visibleHeaders}
          />
        );
      case "Shift View":
        return (
          <ShiftWise
            shiftReports={employeeFilterData}
            shiftLoading={shiftLoading}
            setLeavePage={setLeavePage}
            leavePage={leavePage}
            headers={visibleHeaders}
          />
        );
      default:
        break;
    }
  };

  const handleShiftname = (event, data) => {
    if (data) {
      setShiftName(data?.label);
      setShiftId(data?.id);
      dispatch(setAttendanceShiftId(data?.id));
      dispatch(setAttendanceShiftName(data?.label));
      localStorage.setItem("attendanceShiftId", data?.id);
      localStorage.setItem("attendanceShiftName", data?.label);
      recallApis();
    }
  };

  const recallApis = () => {
    setTimeout(function () {
      reCallDepartmentApi(true);
      reCallShiftApi(true);
    }, 150);
  };

  const handleDateChange = (value) => {
    dispatch(setAttendanceDate(value));
    localStorage.setItem("attendanceDate", value);
    setDate(value);
    recallApis();
  };

  const handleFilteredDataChange = useCallback((filteredData) => {
    setDepartmentFilterData(filteredData);
  }, []);

  const handleFilteredDataChangeForEmployee = useCallback((filteredData) => {
    setEmployeeFilterData(filteredData);
  }, []);

  const reportFilters = (
    <FilterSection
      filterLabel="Filters"
      filters={[
        {
          width: 300,
          props: {
            dataList: departmentDetails.map((obj) => ({
              ...obj,
              label: obj.department_name,
            })),
            selectedValue: departmentName,
            updateDataFunction: handleDepartmentName,
            Icons: PersonOutlineIcon,
            staticLabel: "Department",
          },
        },
        {
          width: 300,
          props: {
            dataList: shiftData?.map((obj) => ({
              ...obj,
              label: obj.name,
            })),
            selectedValue: shiftName,
            updateDataFunction: handleShiftname,
            Icons: PersonOutlineIcon,
            staticLabel: "Shift",
          },
        },
        {
          width: 220,
          component: (
            <ApprovalDateRangeFilter
              singleDatePicker
              startDate={date}
              endDate={date}
              onChange={(pickedDate) => {
                const nextDate = moment(pickedDate).format("YYYY-MM-DD");
                // The picker also reports its initial value on mount; only
                // react to a real change so we don't refetch (or persist) it.
                if (nextDate !== date) handleDateChange(nextDate);
              }}
              showToday={false}
              includeCurrentDate={false}
              allowFutureDate={false}
              staticLabel="Date :"
              sx={filterBarPickerSx}
            />
          ),
        },
      ]}
    />
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      Date: date ? moment(date).format("DD/MM/YYYY") : "N/A",
      Department: departmentName || "All",
      Shift: shiftName || "All",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [date, departmentName, shiftName, topbarCompanyName],
  );

  const activeGroupedHeaders = useMemo(
    () =>
      (selectedComponent === "Department View"
        ? departmentView
        : shiftViewHeader
      ).filter((header) => header.key !== ""),
    [selectedComponent],
  );

  // The filter dropdown/export need each column's type/icon metadata (only
  // present on the flat departmentViewFlat/shiftViewFlat), but the column
  // selection/order/rename must follow the saved view — so merge the two by
  // key rather than picking one source over the other (see DailyPayRun for
  // the same pattern).
  const richHeadersByKey = useMemo(() => {
    const source =
      selectedComponent === "Department View" ? departmentViewFlat : shiftViewFlat;
    const map = new Map();
    source.forEach((header) => map.set(header.key, header));
    return map;
  }, [selectedComponent]);

  const handleSavedViewChange = useCallback(() => {
    setLeavePage(0);
  }, []);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "attendance_reports_saved_views",
    scope: selectedComponent === "Department View" ? "department_view" : "shift_view",
    fullHeaders: activeGroupedHeaders,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  const exportFilterHeaders = useMemo(
    () =>
      toFlatDisplayHeaders(visibleHeaders).map((header) => ({
        ...richHeadersByKey.get(header.key),
        ...header,
      })),
    [visibleHeaders, richHeadersByKey],
  );

  return (
    <React.Fragment>
      {reportFilters}
      {selectedComponent === "Department View" ? (
        <CustomFilter
          title={"Department View"}
          titleOverride={titleOverride}
          filterHeaders={exportFilterHeaders}
          data={departmentReports}
          filteredData={departmentFilterData}
          onFilteredDataChange={handleFilteredDataChange}
          screenAccess={true}
          showtitle_count={true}
          showFilter={false}
          filters={null}
          hideCount={true}
          showSaveButton={false}
          newButtonComponent={newButtonComponent}
          downloadFormats={["excel", "pdf", "csv"]}
          includeMetadata={true}
          metadata={reportMetadata}
        />
      ) : (
        <CustomFilter
          title={"Attendance Record"}
          titleOverride={titleOverride}
          filterHeaders={exportFilterHeaders}
          data={shiftReports}
          filteredData={employeeFilterData}
          onFilteredDataChange={handleFilteredDataChangeForEmployee}
          screenAccess={true}
          showtitle_count={true}
          showFilter={false}
          filters={null}
          hideCount={true}
          showSaveButton={false}
          newButtonComponent={newButtonComponent}
          downloadFormats={["excel", "pdf", "csv"]}
          includeMetadata={true}
          metadata={reportMetadata}
        />
      )}

      {renderComponent()}
    </React.Fragment>
  );
};
